import { useState, useEffect, useMemo, useCallback } from 'react'
import { glorifyApiClient, GlorifyApiError } from '../../../services/api/glorify'
import type { SessionUser } from '../../../services/api/glorify'
import { useDashboardSession } from '../../../hooks/useDashboardSession'

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(isoString: string): string {
  try {
    return new Date(isoString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return isoString
  }
}

function userInitials(displayName: string | undefined, email: string): string {
  if (displayName) {
    return displayName
      .split(' ')
      .map((part) => part[0] ?? '')
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }
  return (email[0] ?? '?').toUpperCase()
}

// ─── Types ────────────────────────────────────────────────────────────────────

type UserFilter = 'all' | 'pending' | 'approved'

// ─── Component ───────────────────────────────────────────────────────────────

export default function AdminUsersPage(): React.JSX.Element {
  const { user: currentUser } = useDashboardSession()

  const [users, setUsers] = useState<SessionUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [filter, setFilter] = useState<UserFilter>('all')
  const [actionError, setActionError] = useState<string | null>(null)
  const [forbiddenAccess, setForbiddenAccess] = useState(false)

  // Auto-dismiss action error after 4 s
  useEffect(() => {
    if (!actionError) return
    const timer = setTimeout(() => setActionError(null), 4000)
    return () => clearTimeout(timer)
  }, [actionError])

  const fetchUsers = useCallback(async (): Promise<void> => {
    setLoadError(null)
    setForbiddenAccess(false)
    try {
      const resp = await glorifyApiClient.adminListUsers()
      setUsers(resp.users)
    } catch (err) {
      if (err instanceof GlorifyApiError && err.status === 403) {
        setForbiddenAccess(true)
      } else {
        setLoadError(
          err instanceof GlorifyApiError ? err.message : 'Failed to load users'
        )
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchUsers()
  }, [fetchUsers])

  // Client-side filter — fetched once; no extra round-trips on filter change
  const filteredUsers = useMemo((): SessionUser[] => {
    switch (filter) {
      case 'pending':  return users.filter((u) => !u.user_is_approved)
      case 'approved': return users.filter((u) => u.user_is_approved)
      default:         return users
    }
  }, [users, filter])

  // ── Optimistic approval toggle ─────────────────────────────────────────────
  const handleApprovalToggle = async (
    userId: number,
    currentlyApproved: boolean
  ): Promise<void> => {
    const snapshot = users
    setUsers((prev) =>
      prev.map((u) =>
        u.user_id === userId ? { ...u, user_is_approved: !currentlyApproved } : u
      )
    )
    try {
      const updated = await glorifyApiClient.adminSetApproval(userId, !currentlyApproved)
      setUsers((prev) => prev.map((u) => (u.user_id === userId ? updated : u)))
    } catch (err) {
      setUsers(snapshot) // rollback
      setActionError(
        `Failed to ${currentlyApproved ? 'revoke' : 'approve'} user: ` +
          (err instanceof GlorifyApiError ? err.message : 'Unknown error')
      )
    }
  }

  // ── Optimistic role change ─────────────────────────────────────────────────
  const handleRoleChange = async (
    userId: number,
    newRole: 'admin' | 'user' | 'partner'
  ): Promise<void> => {
    const snapshot = users
    setUsers((prev) =>
      prev.map((u) => (u.user_id === userId ? { ...u, user_role: newRole } : u))
    )
    try {
      const updated = await glorifyApiClient.adminSetRole(userId, newRole)
      setUsers((prev) => prev.map((u) => (u.user_id === userId ? updated : u)))
    } catch (err) {
      setUsers(snapshot) // rollback
      setActionError(
        `Failed to change role: ` +
          (err instanceof GlorifyApiError ? err.message : 'Unknown error')
      )
    }
  }

  // ── 403 guard — direct URL access by non-admin ────────────────────────────
  if (forbiddenAccess || currentUser.user_role !== 'admin') {
    return (
      <div className="gd-empty">
        <span className="material-icons" aria-hidden="true">lock</span>
        <p>Admin access required to view user management.</p>
      </div>
    )
  }

  return (
    <>
      <div className="gd-page-header">
        <h1>User Management</h1>
        <p>Approve new partners and manage user roles.</p>
      </div>

      {/* Action error banner */}
      {actionError && (
        <div className="gd-action-error" role="alert">
          <span className="material-icons" aria-hidden="true">error_outline</span>
          {actionError}
        </div>
      )}

      {/* Load error */}
      {loadError && (
        <div className="gd-banner gd-banner--error" role="alert">
          <span className="material-icons" aria-hidden="true">warning</span>
          <p>{loadError}</p>
        </div>
      )}

      {/* Filter segmented control */}
      <div className="gd-segmented" role="group" aria-label="Filter users by approval status">
        {(['all', 'pending', 'approved'] as const).map((f) => (
          <button
            key={f}
            className={`gd-segmented__btn${filter === f ? ' gd-segmented__btn--active' : ''}`}
            onClick={() => setFilter(f)}
            aria-pressed={filter === f}
          >
            {f === 'all' ? 'All' : f === 'pending' ? 'Pending' : 'Approved'}
            {f === 'pending' && !isLoading && (
              <span style={{ marginLeft: 6, fontSize: 12, opacity: 0.75 }}>
                ({users.filter((u) => !u.user_is_approved).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="gd-empty">
          <div className="gd-spinner" aria-label="Loading users" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="gd-empty">
          <span className="material-icons" aria-hidden="true">group_off</span>
          <p>
            {filter === 'pending'
              ? 'No users awaiting approval.'
              : filter === 'approved'
                ? 'No approved users yet.'
                : 'No users found.'}
          </p>
        </div>
      ) : (
        <div className="gd-table-wrap">
          <table className="gd-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Approval</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const isSelf = u.user_id === currentUser.user_id
                return (
                  <tr key={u.user_id}>
                    {/* User cell */}
                    <td>
                      <div className="gd-user-cell">
                        <div
                          className="gd-avatar"
                          style={{ width: 32, height: 32, fontSize: 13 }}
                          role="img"
                          aria-label={`Avatar for ${u.display_name ?? u.user_email}`}
                        >
                          {u.avatar_url ? (
                            <img src={u.avatar_url} alt="" />
                          ) : (
                            userInitials(u.display_name, u.user_email)
                          )}
                        </div>
                        <div className="gd-user-cell__info">
                          <b>{u.display_name ?? u.user_email}</b>
                          {u.display_name && (
                            <small>{u.user_email}</small>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Role select */}
                    <td>
                      <select
                        className="gd-role-select"
                        value={u.user_role}
                        disabled={isSelf}
                        aria-label={`Role for ${u.display_name ?? u.user_email}`}
                        onChange={(e) =>
                          void handleRoleChange(
                            u.user_id,
                            e.target.value as 'admin' | 'user' | 'partner'
                          )
                        }
                      >
                        <option value="user">User</option>
                        <option value="partner">Partner</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>

                    {/* Joined date */}
                    <td className="gd-cell-muted">{formatDate(u.user_created_at)}</td>

                    {/* Approval chip */}
                    <td>
                      <span
                        className={
                          u.user_is_approved
                            ? 'gd-status-chip gd-status-chip--completed'
                            : 'gd-status-chip gd-status-chip--pending'
                        }
                      >
                        {u.user_is_approved ? 'Approved' : 'Pending'}
                      </span>
                    </td>

                    {/* Approve / Revoke toggle */}
                    <td>
                      <button
                        className={
                          u.user_is_approved
                            ? 'gd-btn gd-btn--outlined'
                            : 'gd-btn gd-btn--filled'
                        }
                        style={{ height: 34, fontSize: 13, padding: '0 16px' }}
                        disabled={isSelf}
                        onClick={() =>
                          void handleApprovalToggle(u.user_id, u.user_is_approved)
                        }
                        aria-label={
                          u.user_is_approved
                            ? `Revoke access for ${u.display_name ?? u.user_email}`
                            : `Approve ${u.display_name ?? u.user_email}`
                        }
                      >
                        {u.user_is_approved ? 'Revoke' : 'Approve'}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
